import { useCallback, useRef, useState } from 'react';
import { useAtom } from 'jotai';
import type {
  WellborePoint,
  WellboreGeometryEditPoint,
  WellborePointEdit,
  WellborePointsResponse,
} from 'types/api-responses';
import type { PreDrillGeometryStatus } from 'types/pre-drill-workspace';
import {
  wellborePointPath,
  geometryEditsPath,
  datumUpdatesPath,
  type WellboreDatumUpdate,
} from 'types/wellbore-geometry-workspace';
import {
  geometryEditAtom,
  wellborePointUpdateAtom,
  wellborePointDeleteAtom,
} from './atoms';
import type { PropertyDetailsValues } from './property-details-drawer';
import { useUpdateWellboreDatum } from './use-update-wellbore-datum';

interface GeometryWriteHandlers {
  /** A write's answer is the new point set, taken at once so nothing falls back to
   *  the pre-write set while the re-read is on its way. */
  onPointsAnswered: (points: WellborePointsResponse) => void;
  /** A synchronous write answered with a new point set: re-read what derives from it. */
  onPointsChanged: () => void;
  /** A geometry edit landed; `failed` means the points saved but no path was built. */
  onGeometrySettled: (status: PreDrillGeometryStatus | undefined) => void;
  /** The point editor's write landed, so the editor is done with its vertex. */
  onEditorDone: () => void;
  /** A geometry edit was answered; the host decides whether that also closes the sheet. */
  onCommitted: () => void;
  onCommitFailed: () => void;
  /** The datum write landed. */
  onDatumSaved: () => void;
}

interface GeometryWritesOptions extends GeometryWriteHandlers {
  wellWorkspaceId: number | null | undefined;
  uwi14: string;
  pointsData: WellborePointsResponse | null;
  /** The vertex the editor is on, re-read from the latest points by the host. */
  editingPoint: WellborePoint | null;
}

export interface GeometryWrites {
  /** Where a geometry edit goes; null where no Well Workspace addresses the bore. */
  commitHref: string | null;
  /** One edit commits the whole point list in order; depths are never sent. */
  commitGeometry: (
    points: WellboreGeometryEditPoint[],
    landingPointIndex: number | null
  ) => void;
  committing: boolean;
  savePoint: (values: WellborePointEdit) => void;
  pointSaving: boolean;
  deletePoint: (target: WellborePoint) => void;
  pointWriteInFlight: boolean;
  /** One landing point per wellbore: the picked vertex becomes it. */
  setLandingPoint: (target: WellborePoint) => void;
  saveDatum: (values: PropertyDetailsValues) => void;
  datumSaving: boolean;
  geometryEditFailed: boolean;
  dismissGeometryEditFailure: () => void;
  /** Why the last write from the Wellbore Points table failed, shown beside the table;
   *  the backend's reason where it gave one. Null when the last one went through. */
  pointWriteError: string | null;
  dismissPointWriteError: () => void;
  /** Why the last datum save was refused, or null. */
  datumUpdateError: string | null;
  dismissDatumUpdateFailure: () => void;
}

// A geometry edit replaces the whole set, so typed depths and locks travel with their
// points; a derived depth is left out so the derivation owns it.
const toEditPoints = (points: WellborePoint[]): WellboreGeometryEditPoint[] =>
  points.map((point) => ({
    // Named, so the backend carries the row's depth and provenance forward.
    ...(point.id === null ? {} : { id: point.id }),
    latitude: point.latitude,
    longitude: point.longitude,
    point_type: point.pointType,
    tvd_ft: point.depthSource === 'user' ? point.tvdFt : null,
    location_locked: point.locationLocked,
  }));

// Where a point write fails without a reason of its own.
const POINT_NOT_SAVED = 'The wellbore point could not be saved.';

/**
 * The backend's reason for refusing a write, when it gave one.
 * @param {unknown} data The error body.
 * @return {string} The first error's message, or a generic line.
 */
const pointWriteMessage = (data: unknown): string => {
  const errors = (data as { errors?: Array<{ message?: string }> } | undefined)
    ?.errors;
  return errors?.[0]?.message || POINT_NOT_SAVED;
};

/**
 * A point with the editor's changes applied, for a save sent as a geometry edit. A typed
 * depth becomes the point's own; clearing it hands the depth back to the derivation.
 * @param {WellborePoint} point The point as read.
 * @param {WellborePointEdit} values What the editor changed.
 * @return {WellborePoint} The point to send.
 */
const withEdit = (
  point: WellborePoint,
  values: WellborePointEdit
): WellborePoint => {
  const moved = {
    ...point,
    latitude: values.latitude ?? point.latitude,
    longitude: values.longitude ?? point.longitude,
  };
  if (values.clear_typed_depth) {
    return { ...moved, depthSource: 'derived' };
  }
  if (values.tvd_ft !== undefined) {
    return {
      ...moved,
      tvdFt: values.tvd_ft,
      depthSource: values.tvd_ft === null ? 'derived' : 'user',
    };
  }
  return moved;
};

/**
 * Stories 5 and 7: the geometry edit, point update and delete, and datum writes. Each
 * answers with the full points payload; the host's reactions come in as handlers, read
 * through a ref so `commitGeometry` keeps one identity for the map's memoised geometry.
 * @param {GeometryWritesOptions} options The bore, its points and the host's handlers.
 * @return {GeometryWrites} The writes, their in-flight flags and their failure flags.
 */
export const useGeometryWrites = ({
  wellWorkspaceId,
  uwi14,
  pointsData,
  editingPoint,
  ...handlers
}: GeometryWritesOptions): GeometryWrites => {
  const handlersRef = useRef<GeometryWriteHandlers>(handlers);
  handlersRef.current = handlers;

  const [geometryEditState, editGeometry] = useAtom(geometryEditAtom);
  const [pointUpdateState, updateWellborePoint] = useAtom(
    wellborePointUpdateAtom
  );
  const [pointDeleteState, deleteWellborePoint] = useAtom(
    wellborePointDeleteAtom
  );
  const [geometryEditFailed, setGeometryEditFailed] = useState(false);
  const [pointWriteError, setPointWriteError] = useState<string | null>(null);

  // The datum write answers with the whole points payload; the subsea bands and permit
  // floor follow the datum, so the grids are re-read too.
  const datumWrite = useUpdateWellboreDatum({
    href:
      pointsData?.resources.datumUpdates?.href ??
      (wellWorkspaceId ? datumUpdatesPath(wellWorkspaceId, uwi14) : ''),
    onSaved: () => {
      handlersRef.current.onDatumSaved();
      handlersRef.current.onPointsChanged();
    },
  });

  const commitHref =
    pointsData?.resources.geometryEdits.href ??
    (wellWorkspaceId ? geometryEditsPath(wellWorkspaceId, uwi14) : null);

  // The BE rebuilds the geometry (a Synthetic Survey) in the request and answers with
  // the new points, so the edit settles as a landed push would.
  // `fromTable`: the edit came from the Wellbore Points table rather than the map, so a
  // refusal is said beside the table, where the operator is.
  const commit = useCallback(
    (
      points: WellboreGeometryEditPoint[],
      landingPointIndex: number | null,
      fromTable: boolean
    ) => {
      if (!commitHref) return;
      setGeometryEditFailed(false);
      setPointWriteError(null);
      editGeometry({
        type: 'POST',
        url: commitHref,
        data: {
          geometry_edit: {
            landing_point_index: landingPointIndex,
            points,
          },
        },
        onSuccess: (data) => {
          handlersRef.current.onEditorDone();
          if (data) handlersRef.current.onPointsAnswered(data);
          handlersRef.current.onGeometrySettled(data?.geometryStatus);
          handlersRef.current.onCommitted();
        },
        onError: (data) => {
          handlersRef.current.onCommitFailed();
          if (fromTable) setPointWriteError(pointWriteMessage(data));
          else setGeometryEditFailed(true);
        },
      });
    },
    [commitHref, editGeometry]
  );

  const commitGeometry = useCallback(
    (points: WellboreGeometryEditPoint[], landingPointIndex: number | null) =>
      commit(points, landingPointIndex, false),
    [commit]
  );

  const savePoint = (values: WellborePointEdit) => {
    // The vertex went away under the editor, because a re-derive landed. Say so
    // rather than writing to an id that no longer addresses what they edited.
    if (!pointsData || !editingPoint || !wellWorkspaceId) {
      setPointWriteError(POINT_NOT_SAVED);
      return;
    }
    // An untouched bore's seed has no ids to address, so the edit goes out as the
    // whole set with this one point changed.
    if (editingPoint.id === null) {
      commit(
        toEditPoints(
          pointsData.points.map((point) => {
            if (point !== editingPoint) return point;
            return withEdit(point, values);
          })
        ),
        pointsData.landingPointIndex,
        true
      );
      return;
    }
    setPointWriteError(null);
    updateWellborePoint({
      type: 'PATCH',
      url: wellborePointPath(wellWorkspaceId, uwi14, editingPoint.id),
      data: { point: values },
      // The response is the whole re-derived set, so the editor closes on it.
      onSuccess: (data) => {
        handlersRef.current.onEditorDone();
        if (data) handlersRef.current.onPointsAnswered(data);
        handlersRef.current.onPointsChanged();
      },
      onError: (data) => setPointWriteError(pointWriteMessage(data)),
    });
  };

  // The two ends are never deletable; the list is renumbered from the surface down.
  const deletePoint = (target: WellborePoint) => {
    if (!target.deletable || !wellWorkspaceId) return;
    // A seed point has no id, so the delete goes out as the set without it.
    if (target.id === null) {
      if (!pointsData) return;
      const next = pointsData.points.filter((point) => point !== target);
      const landingPointIndex = next.findIndex(
        (point) => point.pointType === 'lp'
      );
      commit(
        toEditPoints(next),
        landingPointIndex === -1 ? null : landingPointIndex,
        true
      );
      return;
    }
    setPointWriteError(null);
    deleteWellborePoint({
      type: 'DELETE',
      url: wellborePointPath(wellWorkspaceId, uwi14, target.id),
      data: {},
      // The answer is the renumbered set, taken at once as the other writes do.
      onSuccess: (data) => {
        if (data) handlersRef.current.onPointsAnswered(data);
        handlersRef.current.onPointsChanged();
      },
      onError: (data) => setPointWriteError(pointWriteMessage(data)),
    });
  };

  // The picked vertex becomes the landing point and the previous one goes back to
  // unassigned, committed as a single edit.
  const setLandingPoint = (target: WellborePoint) => {
    if (!pointsData) return;
    const index = pointsData.points.indexOf(target);
    if (index === -1) return;
    const next = pointsData.points.map((point, at) => {
      if (at === index) return { ...point, pointType: 'lp' as const };
      if (point.pointType !== 'lp') return point;
      return { ...point, pointType: 'unassigned' as const };
    });
    commit(toEditPoints(next), index, true);
  };

  // The two elevations every depth is measured from. An untouched field is left out,
  // so a ground level can be saved without restating the kelly bushing.
  const saveDatum = (values: PropertyDetailsValues) => {
    if (!pointsData || !wellWorkspaceId) return;
    datumWrite.reset();
    const datumUpdate: WellboreDatumUpdate = {};
    if (values.groundLevelFt !== null) {
      datumUpdate.ground_elevation_ft = values.groundLevelFt;
    }
    if (values.kbElevationFt !== null) {
      datumUpdate.datum_elevation_ft = values.kbElevationFt;
    }
    datumWrite.save(datumUpdate);
  };

  return {
    commitHref,
    commitGeometry,
    committing: geometryEditState.loading,
    savePoint,
    // A seed point's save goes out as a geometry edit, so that counts too.
    pointSaving: pointUpdateState.loading || geometryEditState.loading,
    deletePoint,
    pointWriteInFlight: pointUpdateState.loading || pointDeleteState.loading,
    setLandingPoint,
    saveDatum,
    datumSaving: datumWrite.saving,
    geometryEditFailed,
    dismissGeometryEditFailure: () => setGeometryEditFailed(false),
    pointWriteError,
    dismissPointWriteError: () => setPointWriteError(null),
    datumUpdateError: datumWrite.error,
    dismissDatumUpdateFailure: datumWrite.reset,
  };
};
